/**
 * PLUMBING STYLES (2026-10-07, Matt: "the same approach to plumbing fixtures … identifying the
 * general configuration and character of a fixture — like a faucet, a sink basin, a shower or
 * hand shower — and providing a handful of options for each type … Look up real-world
 * dimensions and make the models meet them … Don't use common or builder grade stuff").
 *
 * The light-styles model applied to plumbing: a TYPE is the generic fixture (Toilet, Bath
 * Basin, Kitchen Sink, Lavatory Faucet, Kitchen Faucet, Pot Filler, Tub Filler, Bathtub,
 * Freestanding Tub, Shower Valve, Showerhead, Hand Shower); the STYLE is an INSTANCE choice
 * (one param per kind, so each dropdown lists only its own) that sets the fixture's real
 * size, its 3D body and its plan symbol. The characters were surveyed from the work of Amber
 * Interiors, Mindy Gayer, Studio McGee, Heidi Caillier, Pierce & Ward, Jenni Kayne, Athena
 * Calderone, Jake Arnold, Kelly Wearstler and deVOL — the makers behind them are Waterworks,
 * Kallista, Perrin & Rowe, Lefroy Brooks, Barber Wilsons, Sun Valley Bronze, VOLA, Drummonds,
 * Victoria + Albert, Shaws, Stone Forest, Toto, Duravit and Geberit. Names describe the
 * character, never a brand (Matt). Dimensions come from those makers' spec sheets.
 *
 * Every size here is INCHES in the object frame (objectKinds.ts): x across the width centred on
 * 0 (a tub's LENGTH), y from the anchor (y = 0, the wall or the back) toward the front, z up
 * from the instance base. Counter-sitting kinds (basins, sinks, deck faucets) have z = 0 at the
 * COUNTER TOP; floor kinds at the floor; wall kinds (valves, heads, hand showers, pot fillers) at
 * their mount point. `applyObjectInstanceSize` folds the style's size into the resolved width /
 * depth / height, so the footprint, the pick box, hosting and the plan all follow the style.
 *
 * FINISH: `finish` is the default METAL (a hex for a coated look, or a bare metal token
 * `metal:<key>`) — the Paint Brush recolours a fixture's metal (handles, spouts, legs, feet,
 * valve trims). `body` is the default BODY FINISH (vitreous china, fireclay, stone, cast iron),
 * chosen per instance from BODY_FINISHES.
 */

export interface PlumbingStyle {
  value: string
  label: string
  /** default metal finish token */
  finish: string
  /** default body finish (BODY_FINISHES value) — absent = an all-metal fixture */
  body?: string
  /** width (x), depth (y from the anchor) and height, inches */
  w: number
  d: number
  /** the z span (inches) relative to the instance base */
  z0: number
  z1: number
  /** the widths this style comes in (inches) — the instance picks one; absent = one size */
  sizes?: number[]
  /** how a counter-sitting basin meets its counter: the counter is CUT for an undermount or
   *  apron sink; a vessel sits on top (a drain hole only); a standing basin hosts the floor */
  counter?: 'undermount' | 'apron' | 'vessel' | 'standing'
  /** faucets / fillers: where it mounts */
  mount?: 'deck' | 'wall' | 'floor' | 'rim'
  /** wall-mounted styles: the default mount height above the floor (feet) */
  mountFt?: number
  /** a standing basin's DECK height (inches) — what a faucet placed on it sits on (its z1 can
   *  be a backsplash above that) */
  deck?: number
  /** WALL-HUNG TOILET: how far its concealed parts (the carrier, the tank) reach behind the
   *  finished wall face (inches) — wider and deeper than the room-side footprint by design */
  inWall?: number
}

const NICKEL = 'metal:nickel'
const UNLAQ = 'metal:brass'
const AGED = 'metal:agedBrass'
const BRONZE = 'metal:bronze'
const BLACK = '#1e1e1e'
const STEEL = 'metal:stainless'

/** what a basin, tub or toilet body is made of — chosen per instance */
export const BODY_FINISHES = [
  { value: 'white', label: 'White (Vitreous China / Fireclay)', hex: '#f3f1ec', base: 'china' },
  { value: 'biscuit', label: 'Biscuit', hex: '#e9dcc3', base: 'china' },
  { value: 'matteBlack', label: 'Matte Black', hex: '#2a2a29', base: 'resin' },
  { value: 'stoneWhite', label: 'Stone Resin, Matte White', hex: '#efede6', base: 'resin' },
  { value: 'carrara', label: 'Carrara Marble (Honed)', hex: '#e7e5df', base: 'stone' },
  { value: 'calacatta', label: 'Calacatta Marble (Honed)', hex: '#efeae0', base: 'stone' },
  { value: 'limestone', label: 'Limestone (Honed)', hex: '#d8ceb9', base: 'stone' },
  { value: 'soapstone', label: 'Soapstone', hex: '#5d615e', base: 'stone' },
  { value: 'nero', label: 'Nero Marquina', hex: '#1f1f1f', base: 'stone' },
  { value: 'concrete', label: 'Concrete', hex: '#b4afa5', base: 'resin' },
  { value: 'castIron', label: 'Cast Iron, Painted Exterior (Black)', hex: '#262626', base: 'china' },
] as const

/** the finish materials a fixture's surfaces are tinted over (their gloss): the brushable
 *  METAL (bare metals + RAL / factory coatings, the sheet-metal rule) and the three body
 *  surfaces. Ensured in every model on open (colors.ts#ensureLightFixtureMaterials). */
export const PLUMBING_MATERIALS: Record<string, Record<string, unknown>> = {
  metal: {
    id: 'plumbingMetal01', firmId: 'plumbingMetal01', name: 'Plumbing Fixture Metal', color: '#c8c3b6',
    category: 'Metal', substrate: 'sheetMetal', surface: { color: '#c8c3b6' },
    cut: { pattern: 'solid', fgColor: '#5a5e61', bgColor: '#e4e2de' }, render: { metalness: 0.6, roughness: 0.3 },
  },
  china: {
    id: 'plumbingChina01', firmId: 'plumbingChina01', name: 'Plumbing Fixture, Glazed China', color: '#f3f1ec',
    category: 'Finish', substrate: 'glass', surface: { color: '#f3f1ec' },
    cut: { pattern: 'solid', fgColor: '#8a8478', bgColor: '#f6f4ef' }, render: { metalness: 0, roughness: 0.15 },
  },
  stone: {
    id: 'plumbingStone01', firmId: 'plumbingStone01', name: 'Plumbing Fixture, Honed Stone', color: '#e7e5df',
    category: 'Stone', substrate: 'stone', surface: { color: '#e7e5df' },
    cut: { pattern: 'solid', fgColor: '#8a8478', bgColor: '#efede8' }, render: { metalness: 0, roughness: 0.5 },
  },
  resin: {
    id: 'plumbingResin01', firmId: 'plumbingResin01', name: 'Plumbing Fixture, Matte Resin', color: '#efede6',
    category: 'Finish', substrate: 'glass', surface: { color: '#efede6' },
    cut: { pattern: 'solid', fgColor: '#8a8478', bgColor: '#f3f1ec' }, render: { metalness: 0, roughness: 0.85 },
  },
}

// ---- TOILETS (y = 0 the wall, z = 0 the floor) ----
const TOILET: PlumbingStyle[] = [
  // Kohler Memoirs / Drummonds / Lefroy Brooks — the period close-coupled toilet, elongated,
  // comfort height, a lever on the tank
  { value: 'closeCoupled', label: 'Traditional Close-Coupled', finish: NICKEL, body: 'white', w: 18.9, d: 30.4, z0: 0, z1: 31.1 },
  // Drummonds / Lefroy Brooks / Barber Wilsons — the cistern on brackets high on the wall,
  // an exposed flush pipe, a pull chain
  { value: 'highTank', label: 'High-Tank Pull-Chain', finish: NICKEL, body: 'white', w: 20.0, d: 27.5, z0: 0, z1: 84.0 },
  // Toto Neorest / Duravit — one piece, the trapway concealed behind a smooth skirt, low
  { value: 'skirted', label: 'One-Piece Skirted', finish: NICKEL, body: 'white', w: 18.4, d: 28.5, z0: 0, z1: 22.6 },
  // Toto Aquia / Duravit Starck / Geberit carrier — the bowl floats, the flush plate on the wall
  { value: 'wallHung', label: 'Wall-Hung (In-Wall Carrier)', finish: NICKEL, body: 'white', w: 14.6, d: 21.3, z0: 7.4, z1: 47.6 },
]

// ---- WALL-HUNG TOILETS — the bowl on an in-wall carrier (wallHungToilet.ts holds every rough-in
// number; y = 0 the finished wall, z = 0 the floor; z0..z1 = the carrier's feet to its top at the
// standard 15-3/4" rim — the Rim Height instance setting raises it) ----
const WALL_HUNG_TOILET: PlumbingStyle[] = [
  // Duravit ME by Starck rimless on the DuraSystem carrier with the A1 dual-flush plate — the
  // D-plan bowl, a flat top, a slim lid flush with the ceramic, the plate on the wall above
  { value: 'rimlessD', label: 'Rimless Compact D-Bowl, Dual-Flush Plate', finish: STEEL, body: 'white', w: 14.625, d: 22.5, z0: 0, z1: 45.25, inWall: 4.5 },
]

// ---- BATH BASINS (counter styles: z = 0 the counter top; standing styles: z = 0 the floor) ----
const BASIN: PlumbingStyle[] = [
  { value: 'undermountRect', label: 'Undermount Rectangle', finish: NICKEL, body: 'white', w: 19.8, d: 15.6, z0: -6.8, z1: 0, counter: 'undermount' },
  { value: 'undermountOval', label: 'Undermount Oval', finish: NICKEL, body: 'white', w: 19.3, d: 16.3, z0: -7.5, z1: 0, counter: 'undermount' },
  { value: 'vesselRound', label: 'Carved Stone Vessel (Round)', finish: NICKEL, body: 'carrara', w: 18.0, d: 18.0, z0: 0, z1: 6.0, counter: 'vessel' },
  { value: 'vesselBlock', label: 'Stone Block Vessel', finish: NICKEL, body: 'limestone', w: 24.0, d: 15.0, z0: 0, z1: 5.0, counter: 'vessel' },
  // Waterworks / Lefroy Brooks / Drummonds — a marble top on turned metal legs, a towel rail
  { value: 'washstand', label: 'Marble Washstand on Legs', finish: UNLAQ, body: 'carrara', w: 36.0, d: 22.0, z0: 0, z1: 38.5, counter: 'standing', sizes: [24, 30, 36, 48], deck: 34.5 },
  { value: 'pedestal', label: 'Fluted Pedestal', finish: NICKEL, body: 'white', w: 24.2, d: 19.9, z0: 0, z1: 34.4, counter: 'standing', deck: 34.4 },
  // Stone Forest / Agape — a honed slab trough hung on the wall, the faucet above it
  { value: 'trough', label: 'Wall-Hung Stone Trough', finish: UNLAQ, body: 'limestone', w: 36.0, d: 19.0, z0: 27.5, z1: 34.0, counter: 'standing', sizes: [30, 36, 48, 60], deck: 34.0 },
]

// ---- KITCHEN SINKS (z = 0 the counter top) ----
const KSINK: PlumbingStyle[] = [
  { value: 'apronFireclay', label: 'Fireclay Apron-Front', finish: NICKEL, body: 'white', w: 30.0, d: 18.0, z0: -10.0, z1: 0, counter: 'apron', sizes: [30, 33, 36] },
  { value: 'apronFluted', label: 'Fluted Fireclay Apron-Front', finish: NICKEL, body: 'white', w: 33.0, d: 18.5, z0: -10.0, z1: 0, counter: 'apron', sizes: [24, 30, 33, 36] },
  { value: 'apronStone', label: 'Marble Farmhouse', finish: UNLAQ, body: 'carrara', w: 33.0, d: 20.0, z0: -10.0, z1: 0, counter: 'apron', sizes: [30, 33, 36] },
  { value: 'undermountSteel', label: 'Stainless Undermount (Tight Radius)', finish: STEEL, w: 32.0, d: 18.0, z0: -9.5, z1: 0, counter: 'undermount', sizes: [24, 30, 32, 36] },
  { value: 'prepStone', label: 'Stone Prep Sink', finish: NICKEL, body: 'carrara', w: 15.0, d: 15.0, z0: -7.5, z1: 0, counter: 'undermount', sizes: [15, 18] },
]

// ---- LAVATORY FAUCETS (y = 0 the deck-hole line or the wall face, z = 0 the deck) ----
const LAV_FAUCET: PlumbingStyle[] = [
  // Waterworks Easton / Perrin & Rowe Edwardian / Kallista Bellis — the most-used character
  { value: 'crossWidespread', label: 'Widespread — Cross Handles', finish: NICKEL, w: 10.3, d: 6.3, z0: 0, z1: 5.6, mount: 'deck' },
  { value: 'leverWidespread', label: 'Widespread — Metal Levers', finish: UNLAQ, w: 11.0, d: 6.3, z0: 0, z1: 5.6, mount: 'deck' },
  { value: 'porcelainLever', label: 'Widespread — Porcelain Levers', finish: NICKEL, w: 11.0, d: 6.3, z0: 0, z1: 5.6, mount: 'deck' },
  // Perrin & Rowe Georgian bridge
  { value: 'bridge', label: 'Bridge — Cross Handles', finish: UNLAQ, w: 10.2, d: 6.8, z0: 0, z1: 9.2, mount: 'deck' },
  // Barber Wilsons / Kallista — the "telephone" mixer, a swan neck between two levers
  { value: 'gooseneckLevers', label: 'Single-Hole Gooseneck — Twin Levers', finish: NICKEL, w: 9.4, d: 6.0, z0: 0, z1: 12.2, mount: 'deck' },
  // Waterworks Henry — knurled cylinders, hex nuts, a bent-tube spout
  { value: 'industrial', label: 'Industrial — Knurled Handles', finish: NICKEL, w: 10.4, d: 6.0, z0: 0, z1: 7.4, mount: 'deck' },
  // VOLA / Kallista One — the cylinder body, a tube spout, a disc-and-pin handle
  { value: 'minimal', label: 'Single-Hole — Minimal Cylinder', finish: AGED, w: 2.6, d: 5.6, z0: 0, z1: 7.2, mount: 'deck' },
  { value: 'tallVessel', label: 'Tall Single-Hole (Vessel)', finish: NICKEL, w: 7.4, d: 6.5, z0: 0, z1: 12.0, mount: 'deck' },
  // Sun Valley Bronze / Perrin & Rowe Inca / Waterworks Henry — the wall-mount pair over a
  // trough or vessel, the spout out of its own rosette
  { value: 'wallCross', label: 'Wall-Mount — Cross Handles', finish: UNLAQ, w: 10.0, d: 8.6, z0: 7.2, z1: 10.7, mount: 'wall' },
  { value: 'wallMinimal', label: 'Wall-Mount — Minimal Spout & Lever', finish: AGED, w: 8.6, d: 8.2, z0: 6.75, z1: 12.0, mount: 'wall' },
]

// ---- KITCHEN FAUCETS ----
const K_FAUCET: PlumbingStyle[] = [
  // Perrin & Rowe Ionian / deVOL / Newport Chesterfield — the bridge with its side spray
  { value: 'bridgeSpray', label: 'Bridge — Cross Handles & Side Spray', finish: UNLAQ, w: 21.3, d: 9.4, z0: 0, z1: 16.4, mount: 'deck' },
  { value: 'bridgeLever', label: 'Bridge — Metal Levers', finish: NICKEL, w: 11.1, d: 9.4, z0: 0, z1: 16.4, mount: 'deck' },
  // deVOL Mayan / Waterworks Dash — each handle on its own deck hole, a tall swan spout
  { value: 'deckMixer', label: 'Two-Handle Deck Mixer', finish: AGED, w: 13.0, d: 9.0, z0: 0, z1: 15.4, mount: 'deck' },
  // the bridge on the backsplash, a long swing spout over the apron sink
  { value: 'wallBridge', label: 'Wall-Mount Bridge', finish: UNLAQ, w: 10.5, d: 12.6, z0: 8.0, z1: 14.4, mount: 'wall' },
  { value: 'gooseneck', label: 'Gooseneck — Single Lever', finish: NICKEL, w: 8.4, d: 10.4, z0: 0, z1: 17.0, mount: 'deck' },
  { value: 'preRinse', label: 'Pre-Rinse (Spring Coil)', finish: STEEL, w: 7.6, d: 10.0, z0: 0, z1: 22.0, mount: 'deck' },
]

// ---- POT FILLERS (wall-mounted at the mount height — y = 0 the wall) ----
const POT_FILLER: PlumbingStyle[] = [
  { value: 'articulatedCross', label: 'Articulated — Cross Handle', finish: UNLAQ, w: 17.8, d: 4.6, z0: -2.0, z1: 2.2, mount: 'wall', mountFt: 4.75 },
  { value: 'articulatedMinimal', label: 'Articulated — Minimal', finish: BLACK, w: 17.8, d: 4.6, z0: -2.0, z1: 1.4, mount: 'wall', mountFt: 4.75 },
]

// ---- TUB FILLERS (floor: z = 0 the floor at the filler's base; rim: z = 0 the tub rim) ----
const TUB_FILLER: PlumbingStyle[] = [
  // Waterworks / Perrin & Rowe — twin standpipes, a gooseneck, cross handles, the telephone handset
  { value: 'floorTwin', label: 'Floor-Mount — Twin Standpipes & Hand Shower', finish: NICKEL, w: 10.0, d: 10.4, z0: 0, z1: 38.2, mount: 'floor' },
  // VOLA / Kohler Purist — one column, a curved spout, a slim hand shower
  { value: 'floorColumn', label: 'Floor-Mount — Single Column', finish: AGED, w: 7.2, d: 12.6, z0: 0, z1: 37.0, mount: 'floor' },
  // Waterworks Henry (Athena Calderone) — the exposed mixer on the wall with its hand shower
  { value: 'wallExposed', label: 'Wall-Mount — Exposed Mixer & Hand Shower', finish: UNLAQ, w: 10.3, d: 8.2, z0: 26.9, z1: 33.9, mount: 'wall' },
  // the clawfoot convention — a telephone mixer on the rim
  { value: 'rimTelephone', label: 'Rim-Mount Telephone Mixer (Clawfoot)', finish: NICKEL, w: 10.0, d: 4.7, z0: 0, z1: 8.0, mount: 'rim' },
]

// ---- BATHTUBS — built in (y = 0 the back wall, z = 0 the floor) ----
const TUB: PlumbingStyle[] = [
  { value: 'alcoveApron', label: 'Cast-Iron Alcove with Apron', finish: NICKEL, body: 'white', w: 60.0, d: 32.0, z0: 0, z1: 15.5, sizes: [60, 66] },
  { value: 'dropIn', label: 'Drop-In Soaking (Deck Mount)', finish: NICKEL, body: 'white', w: 66.0, d: 34.0, z0: 0, z1: 22.0, sizes: [60, 66, 72] },
]

// ---- BATHTUBS — freestanding (the tub centred at (0, d/2), z = 0 the floor) ----
const FREE_TUB: PlumbingStyle[] = [
  { value: 'clawfoot', label: 'Cast-Iron Roll-Top Clawfoot', finish: NICKEL, body: 'castIron', w: 66.0, d: 30.3, z0: 0, z1: 25.8, sizes: [60, 66, 72] },
  { value: 'doubleSlipper', label: 'Double-Slipper Clawfoot', finish: NICKEL, body: 'castIron', w: 72.0, d: 30.5, z0: 0, z1: 30.0, sizes: [66, 72] },
  { value: 'bateau', label: 'Bateau (Double-Ended, Skirted)', finish: NICKEL, body: 'white', w: 70.0, d: 30.0, z0: 0, z1: 28.0, sizes: [66, 70] },
  { value: 'ovalStone', label: 'Stone Resin Oval', finish: NICKEL, body: 'stoneWhite', w: 65.0, d: 31.5, z0: 0, z1: 24.0, sizes: [60, 65, 70] },
  { value: 'ovoidTapered', label: 'Stone Resin Ovoid (Tapered Base)', finish: NICKEL, body: 'stoneWhite', w: 70.0, d: 34.0, z0: 0, z1: 21.0, sizes: [66, 70] },
  { value: 'stoneBlock', label: 'Carved Stone Block', finish: NICKEL, body: 'limestone', w: 66.0, d: 32.0, z0: 0, z1: 22.0, sizes: [60, 66, 72] },
]

// ---- SHOWER VALVES (wall-mounted — z about the valve centre, y = 0 the wall) ----
const VALVE: PlumbingStyle[] = [
  { value: 'concealedLever', label: 'Concealed — Round Plate, Metal Lever', finish: NICKEL, w: 7.5, d: 2.2, z0: -3.75, z1: 3.75, mount: 'wall' },
  // Kallista / Waterworks — the thermostatic trim with its volume control below
  { value: 'thermoCross', label: 'Concealed Thermostatic — Cross Handles & Volume', finish: UNLAQ, w: 7.5, d: 2.6, z0: -10.5, z1: 3.75, mount: 'wall' },
  // Lefroy Brooks / Perrin & Rowe / Rohl — the exposed thermostatic bar
  { value: 'exposedBar', label: 'Exposed Thermostatic Bar', finish: NICKEL, w: 13.8, d: 5.1, z0: -1.9, z1: 3.0, mount: 'wall' },
  { value: 'squarePlate', label: 'Concealed — Square Plate, Minimal Lever', finish: AGED, w: 7.0, d: 1.8, z0: -3.5, z1: 3.5, mount: 'wall' },
]

// ---- SHOWERHEADS (wall-mounted at the arm — y = 0 the wall) ----
const HEAD: PlumbingStyle[] = [
  { value: 'rainArm', label: 'Round Rain Head on Wall Arm', finish: NICKEL, w: 10.0, d: 16.0, z0: -2.5, z1: 1.0, mount: 'wall' },
  { value: 'roseGooseneck', label: 'Traditional Rose on Gooseneck Arm', finish: UNLAQ, w: 8.0, d: 14.0, z0: -3.6, z1: 2.1, mount: 'wall' },
  // Waterworks Henry 8" ceiling rose (Athena Calderone) — the drop pipe from a ceiling flange
  { value: 'ceilingRose', label: 'Ceiling-Mount Rose', finish: UNLAQ, w: 8.0, d: 14.0, z0: 0.2, z1: 14.0, mount: 'wall' },
  // Lefroy Brooks / Perrin & Rowe / Waterworks — the exposed set: thermostatic body, rigid
  // riser, the rose on its arm (the head at the mount height, the body ~40" below it)
  { value: 'riserSystem', label: 'Exposed Thermostatic Riser System', finish: NICKEL, w: 13.8, d: 15.4, z0: -41.9, z1: 2.0, mount: 'wall' },
]

// ---- HAND SHOWERS (wall-mounted — z = 0 the slide bar's bottom / the hook) ----
const HAND: PlumbingStyle[] = [
  { value: 'slideBar', label: 'Slide Bar & Hand Shower', finish: NICKEL, w: 6.9, d: 7.0, z0: -5.1, z1: 31.0, mount: 'wall', mountFt: 3.25 },
  { value: 'telephoneHook', label: 'Telephone Hand Shower on Hook', finish: UNLAQ, w: 10.0, d: 4.2, z0: -6.0, z1: 7.9, mount: 'wall', mountFt: 4.0 },
]

/** each styled kind's own list (first = the default) and the instance param that holds it */
/** OUTDOOR GRILLS (2026-10-08, Matt: "a few different high-end barbecue models under the Site
 *  tab that are a good quality like the light fixtures") — the grill rides this same machinery:
 *  a per-instance Style at its real size, a brushable metal, a built-in head cutting its counter.
 *  Characters, never brands: the stainless built-in head and the hybrid-fire cart (the
 *  Kalamazoo / Hestan class), the kamado in a teak table, the Argentine parrilla with its
 *  brasero and cranked V-grates (the Grillworks class), the Santa Maria cart, and the offset
 *  stick smoker (the Texas pit). Built-ins sit 36" counters; heads come 30 / 36 / 42 / 54. */
const GRILL: PlumbingStyle[] = [
  { value: 'builtInGas', label: 'Built-In Gas Grill — Stainless Hood', finish: STEEL, w: 36, d: 27.2, z0: -10.5, z1: 21.7, counter: 'undermount', sizes: [30, 36, 42, 54] },
  { value: 'hybridCart', label: 'Hybrid Fire Grill on Stainless Cart', finish: STEEL, w: 64, d: 31.3, z0: 0, z1: 54.4 },
  { value: 'kamadoTable', label: 'Ceramic Kamado in Teak Table', finish: STEEL, body: 'matteBlack', w: 60, d: 30, z0: 0, z1: 51.6 },
  { value: 'argentine', label: 'Argentine Wood-Fire Grill (Built-In)', finish: STEEL, w: 48, d: 29.5, z0: -8, z1: 24, counter: 'undermount', sizes: [36, 48, 54] },
  { value: 'santaMaria', label: 'Santa Maria Grill on Cart', finish: '#2b2a28', w: 59, d: 31.4, z0: 0, z1: 58 },
  { value: 'offsetSmoker', label: 'Offset Wood Smoker', finish: '#2b2a28', w: 65.2, d: 30, z0: 0, z1: 74.6 },
]

export const PLUMBING_STYLES: Record<string, { param: string; styles: PlumbingStyle[] }> = {
  outdoorGrill: { param: 'grillStyle', styles: GRILL },
  toilet: { param: 'toiletStyle', styles: TOILET },
  wallHungToilet: { param: 'wallHungStyle', styles: WALL_HUNG_TOILET },
  bathSink: { param: 'basinStyle', styles: BASIN },
  kitchenSink: { param: 'sinkStyle', styles: KSINK },
  lavFaucet: { param: 'lavFaucetStyle', styles: LAV_FAUCET },
  kitchenFaucet: { param: 'kitchenFaucetStyle', styles: K_FAUCET },
  potFiller: { param: 'potFillerStyle', styles: POT_FILLER },
  tubFiller: { param: 'tubFillerStyle', styles: TUB_FILLER },
  tub: { param: 'tubStyle', styles: TUB },
  tubFreestanding: { param: 'freeTubStyle', styles: FREE_TUB },
  showerValve: { param: 'valveStyle', styles: VALVE },
  showerHead: { param: 'headStyle', styles: HEAD },
  handShower: { param: 'handShowerStyle', styles: HAND },
}

export const STYLED_PLUMBING_KINDS: readonly string[] = Object.keys(PLUMBING_STYLES)

export function isStyledPlumbingKind(kind: unknown): boolean {
  return typeof kind === 'string' && kind in PLUMBING_STYLES
}

/** the style a fixture is built in — its kind's instance param, else the first */
export function plumbingStyleOf(kind: unknown, params: Record<string, unknown> | undefined): PlumbingStyle | null {
  const k = typeof kind === 'string' ? PLUMBING_STYLES[kind] : undefined
  if (!k) return null
  const v = params?.[k.param]
  return k.styles.find((s) => s.value === v) ?? k.styles[0]
}

/** the width (inches) this instance is built at — its picked size, else the style's own */
export function plumbingWidthIn(style: PlumbingStyle, params: Record<string, unknown> | undefined): number {
  const v = Number(params?.fixtureSizeIn)
  return style.sizes && style.sizes.includes(v) ? v : style.w
}

/** the body finish an instance shows (a BODY_FINISHES entry) */
export function bodyFinishOf(style: PlumbingStyle, params: Record<string, unknown> | undefined) {
  const v = params?.bodyFinish
  return BODY_FINISHES.find((b) => b.value === v) ?? BODY_FINISHES.find((b) => b.value === style.body) ?? BODY_FINISHES[0]
}

export function plumbingStyleChoices(kind: string): { value: string; label: string }[] {
  return (PLUMBING_STYLES[kind]?.styles ?? []).map((s) => ({ value: s.value, label: s.label }))
}

/** every size any style offers (the Size dropdown lists them; a size the style doesn't come
 *  in falls back to the style's own) */
export const PLUMBING_SIZE_CHOICES = [15, 18, 24, 30, 32, 33, 36, 42, 48, 54, 60, 65, 66, 70, 72]
  .map((n) => ({ value: String(n), label: `${n}"` }))

/** kinds whose styles come in sizes */
export const SIZED_PLUMBING_KINDS: readonly string[] =
  STYLED_PLUMBING_KINDS.filter((k) => PLUMBING_STYLES[k].styles.some((s) => s.sizes?.length))

/** the shared finish material a fixture's metal colour resolves over (the lights precedent) */
export const PLUMBING_METAL_MATERIAL_ID = 'plumbingMetal01'
export const plumbingBodyMaterialId = (base: string): string => String((PLUMBING_MATERIALS[base] ?? PLUMBING_MATERIALS.china).id)
